import { Deal } from '@/hooks/useDeals';
import CountdownTimer from './CountdownTimer';

interface DealBannerProps {
  deal: Deal;
}

export default function DealBanner({ deal }: DealBannerProps) {
  const isExpiringSoon = () => {
    const diff = new Date(deal.end_at).getTime() - Date.now();
    return diff > 0 && diff < 1000 * 60 * 60 * 24;
  };

  const urgent = isExpiringSoon();

  return (
    <div
      className="relative overflow-hidden rounded-3xl p-8 md:p-14 text-center"
      style={{
        background: `linear-gradient(135deg, ${deal.banner_color}18 0%, ${deal.banner_color}08 60%, rgba(10,10,10,0.9) 100%)`,
        border: `1px solid ${deal.banner_color}30`,
        boxShadow: `0 0 60px ${deal.banner_color}10`,
      }}
    >
      {/* Background radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, ${deal.banner_color}15 0%, transparent 65%)`,
        }}
      />

      {/* Top shimmer line */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(to right, transparent, ${deal.banner_color}80, transparent)` }}
      />

      {/* Corner accents */}
      <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 rounded-tr-3xl pointer-events-none"
        style={{ borderColor: `${deal.banner_color}40` }}></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 rounded-bl-3xl pointer-events-none"
        style={{ borderColor: `${deal.banner_color}40` }}></div>

      <div className="relative z-10">
        {/* Discount badge */}
        {deal.discount_percent > 0 && (
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-black mb-6"
            style={{
              background: `${deal.banner_color}20`,
              color: deal.banner_color,
              border: `1px solid ${deal.banner_color}50`,
              boxShadow: `0 0 20px ${deal.banner_color}15`,
            }}
          >
            <i className="ri-flashlight-fill"></i>
            خصم {deal.discount_percent}% على الكل
          </div>
        )}

        {/* Title */}
        <h2
          className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight"
          style={{
            fontFamily: "'Amiri', 'Tajawal', serif",
            filter: `drop-shadow(0 0 30px ${deal.banner_color}30)`,
          }}
        >
          {deal.title}
        </h2>

        {/* Subtitle */}
        {deal.subtitle && (
          <p
            className="text-gray-300 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}
          >
            {deal.subtitle}
          </p>
        )}

        {/* Countdown label */}
        <p
          className={`text-xs tracking-[0.3em] uppercase mb-4 ${urgent ? 'text-red-400' : 'text-gray-400'}`}
          style={{ fontFamily: "'Tajawal', sans-serif" }}
        >
          {urgent ? 'ينتهي العرض قريباً!' : 'ينتهي العرض خلال'}
        </p>

        {/* Countdown */}
        <CountdownTimer endAt={deal.end_at} />
      </div>
    </div>
  );
}